import { api, req } from "../api.ts";
import { esc, h } from "../dom.ts";
import { readPlayer } from "../play/client.ts";
import { playPage } from "./play.ts";
import { emailSignIn } from "./emailSignIn.ts";
import { localeTag, t } from "../i18n/index.ts";
import { listBotGames } from "../game/botHistory.ts";

interface Entry { id:string; player_id:string; white_name:string; black_name:string; lifecycle:string; status:string; saved:boolean; retained:boolean; expires_at:number|null; abandon_at:number|null }
/** A segment handed back to Messages is over here but not finished: say so. */
const stateLabel = (game: Entry): string => t("library." + (game.status === "returned" ? "returned" : game.lifecycle));
export function saveGamesPanel(done?: () => void, game?: {id:string;player_id:string}): HTMLElement {
  const panel = h(`<details class="club-save"><summary>${t("library.saveGames")}</summary><p>${t("library.saveHelp")}</p><div data-email></div><p class="msg" role="status"></p></details>`);
  void readPlayer().then(async player => {
    if (player?.kind === "account") {
      panel.querySelector("p")!.textContent = t("library.recoverHelp");
      if (!game) { panel.querySelector("[data-email]")!.textContent = t("library.saveEach"); return; }
      const button = h(`<button class="btn primary">${t("library.saveThis")}</button>`) as HTMLButtonElement;
      button.onclick = async () => {
        button.disabled = true;
        try {
          await req("POST",`/api/play/games/${game.id}/save`,{actor_id:game.player_id});
          panel.querySelector(".msg")!.textContent = t("library.saved"); button.remove(); done?.();
        } catch { panel.querySelector(".msg")!.textContent = t("library.saveFailed"); button.disabled = false; }
      };
      panel.querySelector("[data-email]")!.append(button); return;
    }
    const config = await api.getConfig();
    if (!config.email_signin_enabled) { panel.querySelector(".msg")!.textContent = t("library.emailDisabled"); return; }
    panel.querySelector("[data-email]")!.append(emailSignIn(config.registration_mode === "public",{saveGames:true,onSuccess:() => {
      panel.querySelector("[data-email]")!.replaceChildren();
      panel.querySelector(".msg")!.textContent = t("library.savedRecover");
      done?.();
    }}));
  }).catch(() => { panel.querySelector(".msg")!.textContent = t("library.emailFailed"); });
  return panel;
}
export function myGamesScreen(): HTMLElement {
  const root = playPage(`<h1>${t("nav.myGames")}</h1><p data-identity></p><p><a data-link href="/settings">${t("settings.title")}</a></p><div data-save></div><h2>${t("library.webGames")}</h2><div data-games></div><button class="btn ghost" data-more hidden>${t("library.more")}</button><p class="msg" role="status" aria-live="polite" data-library-message></p>`,"games");
  const local = h('<section class="local-games"><h2></h2><p class="fine"></p><div data-local-list></div></section>');
  local.querySelector("h2")!.textContent = t("library.computer");
  local.querySelector("p")!.textContent = t("library.localHelp");
  const records = listBotGames(), list = local.querySelector("[data-local-list]")!;
  if (!records.length) list.textContent = t("library.noComputer");
  for (const game of records) {
    const row = document.createElement("article"); row.className = "club-game";
    const title = document.createElement("a"); title.dataset.link = ""; title.href = "/game/bot/" + encodeURIComponent(game.id);
    title.textContent = t("chess.computer") + " · " + t("levels.levelN", {n: game.level});
    const heading = document.createElement("h3"); heading.append(title);
    const detail = document.createElement("p");
    detail.textContent = [t(game.result === "won" ? "result.youWon" : game.result === "lost" ? "result.youLost" : "result.drawWord"), game.tc.includes("+") ? game.tc : t("clock.untimed"), new Date(game.createdAt).toLocaleString(localeTag())].join(" · ");
    const analyze = document.createElement("a"); analyze.className = "btn ghost"; analyze.dataset.link = "";
    analyze.href = "/analysis/bot/" + encodeURIComponent(game.id); analyze.textContent = t("game.analyze");
    row.append(heading, detail, analyze); list.append(row);
  }
  root.querySelector("main")!.append(local);
  let offset = 0; let loading = false;
  const more = root.querySelector<HTMLButtonElement>("[data-more]")!;
  root.querySelector("[data-save]")!.append(saveGamesPanel(() => { offset=0; root.querySelector("[data-games]")!.replaceChildren(); void load(); }));
  async function load() {
    if (loading) return; loading = true; more.disabled = true;
    try {
      root.querySelector("[data-library-message]")!.textContent = ""; more.textContent = t("library.more");
      const player = await readPlayer();
      root.querySelector("[data-identity]")!.textContent = player ? t(player.kind === "account" ? "library.signedIn" : "library.guest", {name: player.username}) : t("library.noSession");
      if (!player) return;
      const result = await req<{games:Entry[];has_more:boolean}>("GET",`/api/play/games?offset=${offset}`);
      const list = root.querySelector<HTMLElement>("[data-games]")!;
      if (!result.games.length && offset === 0) list.textContent = t("library.empty");
      for (const game of result.games) {
        const retention = game.saved ? t("library.saved") : game.expires_at ? t("library.temporaryUntil", {date: new Date(game.expires_at*1000).toLocaleString(localeTag())}) : t(game.retained ? "library.retained" : "library.temporary");
        const row = h(`<article class="club-game"><h2><a data-link href="/game/${esc(game.id)}">${esc(game.white_name)} · ${esc(game.black_name)}</a></h2><p>${esc(stateLabel(game))} · ${esc(retention)}</p><a class="btn ghost" href="/api/play/games/${esc(game.id)}/pgn" download>${t("analysis.downloadPgn")}</a>${game.lifecycle === "finished" ? `<a class="btn ghost" data-link href="/analysis/game/${esc(game.id)}">${t("game.analyze")}</a>` : ""}${player.kind === "account" && !game.saved ? `<button class="btn" data-save-game>${t("library.saveGame")}</button>` : ""}</article>`);
        const button = row.querySelector<HTMLButtonElement>("[data-save-game]");
        if (button) button.onclick = async () => {
          button.disabled = true;
          try { await req("POST",`/api/play/games/${game.id}/save`,{actor_id:game.player_id}); row.querySelector("p")!.textContent = stateLabel(game) + " · " + t("library.saved"); button.remove(); }
          catch { root.querySelector("[data-library-message]")!.textContent = t("library.saveFailed"); button.disabled = false; }
        };
        list.append(row);
      }
      offset += result.games.length; more.hidden = !result.has_more;
    } catch { root.querySelector("[data-library-message]")!.textContent = t("library.failed"); more.hidden = false; more.textContent = t("common.retry"); }
    finally { loading = false; more.disabled = false; }
  }
  more.onclick = () => void load(); void load(); return root;
}
